import { PureAbility } from '@casl/ability';
import { ApiResponse } from 'apisauce';
import { StatusType } from '../orders/order.types';
import { UserRole } from './roles';

export type ActionType = 'create' | 'read' | 'update' | 'delete' | 'menu';

export interface AbilityRule {
  action: ActionType | ActionType[];
  subject: ResourceType | ResourceType[];
  fields?: string | string[];
  inverted?: boolean;
  reason?: string;
}

export interface UserTeamConditions {
  productColorClassificationIds?: string[];
  priceCompositionsIds?: string[];
  priceTableIds?: string[];
  orderStatus?: StatusType[];
}

export enum RoleBehaviorCapability {
  CATALOGS_MANAGE_BRAND_CATALOGS = 'CATALOGS_MANAGE_BRAND_CATALOGS',
  CATALOGS_SHOW_INACTIVE_CATALOGS = 'CATALOGS_SHOW_INACTIVE_CATALOGS',
  PRODUCTS_SHOW_UNAVAILABLE_FOR_SALE = 'PRODUCTS_SHOW_UNAVAILABLE_FOR_SALE',
  PRODUCTS_SHOW_WITH_ZERO_PRICE = 'PRODUCTS_SHOW_WITH_ZERO_PRICE',
  INVITES_MANAGE_INVITES = 'INVITES_MANAGE_INVITES',
  OFFLINE_EXPORT_ORDER_PDF = 'OFFLINE_EXPORT_ORDER_PDF',
  PROMOTIONS_LINK_USER_CONTEXT_GROUPS = 'PROMOTIONS_LINK_USER_CONTEXT_GROUPS',
  TARGETS_VIEW_ALL_BRAND_TARGETS = 'TARGETS_VIEW_ALL_BRAND_TARGETS',
}

export interface RoleBehaviorSettings {
  capabilities: RoleBehaviorCapability[];
}

export interface GetAbilitiesData {
  userId: string;
  teamId: string;
  brandId?: string;
  storeId?: string;
  role: UserRole;
  abilities: AbilityRule[];
  conditions?: UserTeamConditions;
  adminTeamId?: string;
  customRole: boolean;
  userContextGroupId?: string;
  behaviorSettings?: RoleBehaviorSettings;
}

export type GetAbilitiesResponse = Pick<ApiResponse<GetAbilitiesData>, 'ok' | 'status' | 'data'>;

export interface Ability {
  can: (action: ActionType, subject: ResourceType) => boolean;
  cannot: (action: ActionType, subject: ResourceType) => boolean;
  getAbilities: () => Promise<void>;
  getConditions: () => UserTeamConditions | undefined;
  hasCapability: (capability: RoleBehaviorCapability) => boolean;
  getUserContextGroupId: () => string | undefined;
  isCustomRole: () => boolean;

  getAbilityApi: () => PureAbility;
  resetAbilities: () => void;
  isLoadingAbilities?: boolean;
}

export type AbilityContextProviderProps = {
  children: React.ReactNode;
};

export type RolesActionTypes =
  | 'move-order-status-to-sent'
  | 'move-order-status-to-approved'
  | 'move-order-status-to-canceled'
  | 'move-order-status-to-rejected';

export type ResourceType =
  | RolesActionTypes
  | 'admin'
  | 'admin-options'
  | 'abilities'
  | 'all'
  | 'announcements'
  | 'banners'
  | 'brand-split-settings'
  | 'brands-address'
  | 'brands-general-settings'
  | 'brands-licenses'
  | 'brands-users'
  | 'brands'
  | 'business-policies'
  | 'catalog-products'
  | 'catalog-sections'
  | 'catalogs'
  | 'categories'
  | 'cases'
  | 'chats'
  | 'classifications'
  | 'collections'
  | 'colors'
  | 'connections'
  | 'consultants'
  | 'contacts'
  | 'commercial-policy'
  | 'customer-groups-payment-methods'
  | 'customer-groups-price-tables'
  | 'customer-groups'
  | 'customers-address'
  | 'customers-contact'
  | 'customer-medias'
  | 'customers-payment-methods'
  | 'customer-sales-representatives'
  | 'customer-classifications'
  | 'customers'
  | 'delivery-windows'
  | 'events'
  | 'force-reverse-order'
  | 'freight-type'
  | 'future-stocks'
  | 'grids'
  | 'imports'
  | 'instagram'
  | 'integrations'
  | 'force-sync-products'
  | 'force-sync-stocks'
  | 'force-sync-customers'
  | 'force-sync-shipping-companies'
  | 'force-sync-material-stocks'
  | 'invites'
  | 'suggested-invites'
  | 'received-invites'
  | 'invoices'
  | 'my-network-explore'
  | 'my-network-profile'
  | 'my-network'
  | 'net-promoter-score'
  | 'highlights'
  | 'order-conditions'
  | 'order-delivery-window'
  | 'order-history'
  | 'order-integration'
  | 'order-items'
  | 'order-details'
  | 'order-product-packs'
  | 'order-payments'
  | 'orders'
  | 'home-page-orders'
  | 'page-orders'
  | 'page-customers'
  | 'page-pre-order'
  | 'params'
  | 'payment-methods'
  | 'price-compositions'
  | 'price-tables'
  | 'product-categories'
  | 'product-colors'
  | 'kits'
  | 'locals'
  | 'product-medias'
  | 'product-sizes'
  | 'products'
  | 'profile'
  | 'promotions'
  | 'materials'
  | 'material-consumption'
  | 'report-product-sales'
  | 'report-period-sales'
  | 'report-supplier-sales'
  | 'reports'
  | 'sales-opportunities'
  | 'sales-targets'
  | 'sales-representatives'
  | 'search'
  | 'segments'
  | 'sellers'
  | 'settings'
  | 'shipping-companies'
  | 'shared-entities'
  | 'showroom'
  | 'sign-up'
  | 'sizes'
  | 'sku-availabilities'
  | 'skus-pre-order'
  | 'skus'
  | 'slug'
  | 'stock-available-amount'
  | 'stock-movements'
  | 'stock'
  | 'storefront'
  | 'stores-address'
  | 'stores-customers'
  | 'stores-licenses'
  | 'store-points-of-sale'
  | 'store-integration-config'
  | 'stores-users'
  | 'stores'
  | 'system-parameter'
  | 'tags'
  | 'targets'
  | 'teams'
  | 'user-daily-activity'
  | 'catalog-customers'
  | 'catalog-sales-representatives'
  | 'catalog-classification-permissions'
  | 'mercadological-tree'
  | 'catalog-sell-in-price-filter'
  | 'municipality'
  | 'user-context-preferences'
  | 'user-context-groups'
  | 'product-color-pack-stocks'
  | 'export-order-pdf'
  | 'export-order-csv'
  | 'send-customer-from-draft-to-pending'
  | 'send-customer-from-draft-to-inactive'
  | 'send-customer-from-draft-to-approved'
  | 'send-customer-from-pending-to-approved'
  | 'send-customer-from-pending-to-draft'
  | 'send-customer-from-pending-to-inactive'
  | 'send-customer-from-approved-to-inactive'
  | 'send-customer-from-approved-to-blocked'
  | 'send-customer-from-inactive-to-draft'
  | 'send-customer-from-inactive-to-pending'
  | 'send-customer-from-blocked-to-approved'
  | 'payment-gateways'
  | 'accounts-receivable'
  | 'integrations-webhooks'
  | 'tms-integration-configs'
  | 'product-matches'
  | 'product-registrations'
  | 'lightdash-reports'
  | 'store-orders'
  | 'rachel'
  | 'offline';
